import React from "react";
import styled, { css } from "styled-components";
import moment from "moment-timezone";
import { useMappedState } from "redux-react-hook";
import { toast } from "react-toastify";

import { PageProps } from "src/pages/Router/routes";
import {
  Layout,
  MainContentWrapper
} from "src/core/shared/components/Layout";
import { Text } from "src/library/components/Text";
import { DateInput } from "src/core/shared/components/Date/DateInput";
import ListWastage, { ListWastageRefData } from "src/modules/waste/components/ListWastage";
import { formatDate } from "src/library/utils";
import {
  Wastage,
  useWasteApi
} from "src/modules/waste/api/waste.api";
import { Button } from "src/library/components/Button";
import { IStore } from "src/core/shared/store";
import { ErrorNotice } from "src/core/shared/components/ErrorNotice";
import { useProductsApi } from "src/core/products/api/products.api";

const RecordWastePage: React.ReactFCWithChildren<PageProps<unknown>> = ({ title, ...props }) => {
  const location = props.location;
  const listWastageRef = React.useRef<ListWastageRefData>(null);
  const [ getBranchWastageRes, getBranchWastageReq ] = useWasteApi("GET_BRANCH_WASTAGE");
  const [ postBranchWastageRes, postBranchWastageReq ] = useWasteApi("SET_BRANCH_WASTAGE");
  const [ historicllyOrderedProductsRes, historicllyOrderedProductsReq ] = useProductsApi("GET_HISTORICALLY_ORDERED_PRODUCTS_BY_BRANCH");
  const [ historicalWastage, setHistoricalWastage ] = React.useState<Wastage>([]);
  const [ clearListWastageData, setClearListWastageData ] = React.useState(false);

  // Check location state for a provided date
  const locationStateWasteDate: Date | undefined = React.useMemo(() => {
    return location.state?.wasteDate ? moment.utc(location.state.wasteDate).toDate() : undefined;
  }, [ location ]);

  const [ dateOfWastage, setDateOfWastage ] = React.useState(() =>
    locationStateWasteDate || moment.utc().toDate()
  );

  const { currentDrop, terminology } = useMappedState(({ global, drops }: IStore) => ({
    currentDrop: drops.drop,
    terminology: global.settings?.terminology
  }));

  const loading = React.useMemo(() => (
    getBranchWastageRes.pending ||
    postBranchWastageRes.pending ||
    historicllyOrderedProductsRes.pending
  ), [
    getBranchWastageRes.pending,
    postBranchWastageRes.pending,
    historicllyOrderedProductsRes.pending
  ]);

  const handleSaveWastage = React.useCallback(() => {
    if (!currentDrop?.id) {
      toast.error("Unable to save wastage: drop not found.");

      return;
    }

    // Don't attempt create if no historical wastage recorded AND no new products have been added
    if (!listWastageRef.current?.currentWastage.length && !historicalWastage.length) {
      toast.error("No products have been added. Please add products before saving.");

      return;
    }

    postBranchWastageReq({
      branchId: currentDrop.id,
      wasteDate: dateOfWastage,
      // Historical wastage gets merged into currentWastage when loaded.
      // If current wastage is empty, we can assume user removed all products and can go ahead with the update.
      wastage: listWastageRef.current?.currentWastage || []
    });
  }, [
    postBranchWastageReq,
    currentDrop?.id,
    dateOfWastage,
    historicalWastage
  ]);

  // When wastage is saved, update state
  React.useEffect(() => {
    if (postBranchWastageRes.data) {
      toast.success("Wastage saved successfully.");
      // Clear table data
      setClearListWastageData(true);
      // Update historical waste data
      setHistoricalWastage(postBranchWastageRes.data);
    }

    if (postBranchWastageRes.error) {
      toast.error(postBranchWastageRes.error.message);
    }
  }, [ postBranchWastageRes.data, postBranchWastageRes.error ]);

  // On dateOfWastage change
  React.useEffect(() => {
    // Clear table data anytime date changes
    setClearListWastageData(true);

    if (currentDrop?.id) {
      // Update available products
      historicllyOrderedProductsReq({
        branchId: currentDrop.id,
        range: { endDate: dateOfWastage }
      });

      // Fetch wastage for date
      getBranchWastageReq({
        branchId: currentDrop.id,
        filters: { wasteDate: dateOfWastage }
      });
    }
  }, [
    currentDrop?.id,
    dateOfWastage,
    getBranchWastageReq,
    historicllyOrderedProductsReq
  ]);

  // When wastage is fetched, update historical wastage state
  React.useEffect(() => {
    if (getBranchWastageRes.data) {
      setHistoricalWastage(getBranchWastageRes.data);
    }

    if (getBranchWastageRes.error) {
      toast.error(getBranchWastageRes.error.message);
    }
  }, [ getBranchWastageRes.data, getBranchWastageRes.error ]);

  return (
    <Layout
      title={title}
      {...props}
    >
      <MainContentWrapper>
        <StyledWrapper>
          {!currentDrop?.id || !terminology && (
            <ErrorNotice
              message={`Unable to load data. ${!currentDrop?.id ? "Drop not found." : ""} ${!terminology ? "Terminology not found." : ""}`}
              type="error"
            />
          )}

          <div className="section">
            <Text as="h4">
              Date Of Waste:
            </Text>

            <DateInput
              label="Select Date"
              date={dateOfWastage}
              setDate={date => setDateOfWastage(date)}
              minimumDate={moment.utc().subtract(10, "years").toDate()}
              maximumDate={moment.utc().toDate()}
            />
          </div>

          <div className="section">
            <Text as="h4">
              Products:
            </Text>

            <div className="info">
              <Text>
                {`Below lists products that are noted as wasted on ${formatDate(dateOfWastage, "ddd D MMM YYYY")}.`}
              </Text>
            </div>

            {currentDrop?.id && terminology && (
              <ListWastage
                ref={listWastageRef}
                availableProducts={historicllyOrderedProductsRes.data || []}
                branchId={currentDrop.id}
                terminology={terminology}
                historicalWastage={historicalWastage}
                wasteDate={dateOfWastage}
                isLoading={postBranchWastageRes.pending || getBranchWastageRes.pending}
                clearData={clearListWastageData}
                updateClearData={setClearListWastageData}
              />
            )}
          </div>

          <div className="section">
            <Button
              onClick={handleSaveWastage}
              loading={loading}
              disabled={postBranchWastageRes.pending}
            >
              Save
            </Button>
          </div>
        </StyledWrapper>
      </MainContentWrapper>
    </Layout>
  );
};

const StyledWrapper = styled.div`
  ${({ theme }) => css`
    .section {
      margin-bottom: ${theme.spacing.margins.lg};
    }

    .info {
      margin-bottom: ${theme.spacing.margins.md};
    }
  `}
`;

export default RecordWastePage;